import { PlayerRef } from '@remotion/player';
import React, { useMemo } from 'react';
import {
  PLAYHEAD_WIDTH,
  TIMELINE_HEADER_HEIGHT,
  TIMELINE_HORIZONTAL_PADDING,
} from '../constants';
import { useKeyboardControls } from '../utils/keyboard-controls';
import { getItemLeftOffset } from '../utils/position-utils';
import { playheadRef } from '../utils/restore-scroll-after-zoom';
import { useFps, useTimelineHeight } from '../utils/use-context';
import { useTimelinePosition } from '../utils/use-timeline-position';
import { Z_INDEX_PLAYHEAD } from '../z-indices';

const CAP_PATH_D =
  'M4 1H8C9.65685 1 11 2.34315 11 4V20C11 21.6569 9.65685 23 8 23H4C2.34315 23 1 21.6569 1 20V4C1 2.34315 2.34315 1 4 1Z';

const INDICATOR_BAR_X = 5;
const INDICATOR_BAR_Y = 24;
const INDICATOR_BAR_WIDTH = 2;

const makePlayheadPath = (height: number) => {
  return {
    capPath: CAP_PATH_D,
    barHeight: Math.max(height, 0),
  };
};

const PLAYHEAD_SVG_WIDTH = 12;
const PLAYHEAD_SVG_HEIGHT = 32;

export const Playhead: React.FC<{
  visibleFrames: number;
  playerRef: React.RefObject<PlayerRef | null>;
  height: number;
  timelineWidth: number;
  durationInFrames: number;
  overrideFrame?: number | null;
}> = ({
  playerRef,
  visibleFrames,
  height,
  timelineWidth,
  durationInFrames,
  overrideFrame,
}) => {
  const timelinePosition = useTimelinePosition({ playerRef });
  const { fps } = useFps();
  useKeyboardControls({ playerRef, fps });

  const displayPosition = overrideFrame ?? timelinePosition;

  // 마지막 frame인 경우, playhead를 frame의 오른쪽에 렌더링
  // 정렬되어 있어서 더 보기 좋음
  const leftToDisplay =
    displayPosition === durationInFrames - 1 && displayPosition > 0
      ? displayPosition + 1
      : displayPosition;

  const left =
    getItemLeftOffset({
      from: leftToDisplay,
      totalDurationInFrames: visibleFrames,
      timelineWidth,
    }) + TIMELINE_HORIZONTAL_PADDING;

  const style = useMemo(() => {
    return {
      left,
      marginLeft: -PLAYHEAD_WIDTH / 2,
    };
  }, [left]);

  const timelineHeight = useTimelineHeight();

  const { capPath, barHeight } = useMemo(() => {
    return makePlayheadPath(
      Math.max(height, timelineHeight + TIMELINE_HEADER_HEIGHT) /
        (PLAYHEAD_WIDTH / PLAYHEAD_SVG_WIDTH),
    );
  }, [height, timelineHeight]);

  const svgStyle = useMemo(() => {
    return {
      width: PLAYHEAD_WIDTH,
      aspectRatio: `${PLAYHEAD_SVG_WIDTH} / ${PLAYHEAD_SVG_HEIGHT}`,
      overflow: 'visible',
      marginTop: 0,
    };
  }, []);

  const stickyStyle: React.CSSProperties = useMemo(() => {
    return {
      position: 'sticky',
      zIndex: Z_INDEX_PLAYHEAD,
      top: 0,
    };
  }, []);

  return (
    <div
      className="pointer-events-none absolute top-0 flex h-full flex-col items-center"
      style={style}
      ref={playheadRef}
      id="playhead"
    >
      <div style={stickyStyle}>
        <svg
          style={svgStyle}
          viewBox={`0 0 ${PLAYHEAD_SVG_WIDTH} ${PLAYHEAD_SVG_HEIGHT}`}
          fill="none"
        >
          <path
            d={capPath}
            fill="white"
            stroke="var(--sys-style-primary-default)"
            strokeWidth={2}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
          <rect
            x={INDICATOR_BAR_X}
            y={INDICATOR_BAR_Y}
            width={INDICATOR_BAR_WIDTH}
            height={barHeight}
            fill="var(--sys-style-primary-default)"
          />
        </svg>
      </div>
    </div>
  );
};
